import React, { useCallback, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { useAlerts } from '../alert/AlertContext';
import api from '../common/api';

export type AddActivityFormPayload = {
  inmateId: number;
  amount: number;
  description: string;
  notifyInmate: boolean;
  chargeRental: boolean;
};

type AddActivityFormProps = {
  inmateId: number;
  accountType: 'wallet' | 'audio-video';
  onSuccess: () => void;
  disabled?: boolean;
};

const AddActivityForm: React.FC<AddActivityFormProps> = (props) => {
  const { inmateId, accountType, onSuccess, disabled } = props;
  const [amount, setAmount] = useState<string>('');
  const [addOrRemove, setAddOrRemove] = useState<'add' | 'remove'>('add');
  const [description, setDescription] = useState<string>('');
  const [notifyInmate, setNotifyInmate] = useState<boolean>(false);
  const [chargeRental, setChargeRental] = useState<boolean>(true);
  const [adding, setAdding] = useState<boolean>(false);
  const alerts = useAlerts();

  const amountNum = parseFloat(amount);
  const amountValid = amount !== '' && !isNaN(amountNum) && amountNum >= 0;
  const canSubmit = amountValid && !adding && !disabled;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) {
        return;
      }
      const signedAmount = addOrRemove === 'add' ? -Math.abs(amountNum) : Math.abs(amountNum);
      const payload: AddActivityFormPayload = {
        inmateId,
        amount: signedAmount,
        description: description.trim(),
        notifyInmate,
        chargeRental: accountType === 'wallet' ? chargeRental : true,
      };
      setAdding(true);
      try {
        const endpoint = accountType === 'audio-video' ? '/inmate-account/audio-video/activity' : '/inmate-account/activity';
        await api.post(endpoint, payload);
        alerts.success('The activity record was successfully created');
        setAmount('');
        setDescription('');
        setAddOrRemove('add');
        setNotifyInmate(false);
        setChargeRental(true);
        onSuccess();
      } catch {
        alerts.error('An error occurred while creating activity record');
      } finally {
        setAdding(false);
      }
    },
    [
      canSubmit,
      inmateId,
      addOrRemove,
      amountNum,
      description,
      notifyInmate,
      chargeRental,
      accountType,
      onSuccess,
    ]
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Card className="card-info">
        <Card.Header>Add Activity Record</Card.Header>
        <Card.Body>
          <div className="row">
            <div className="col-md-3">
              <Form.Group className="mb-2">
                <Form.Label>Amount</Form.Label>
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="text"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    isInvalid={amount !== '' && !amountValid} />
                  <div className="btn-group" role="group">
                    <Button
                      type="button"
                      variant={addOrRemove === 'add' ? 'primary' : 'secondary'}
                      onClick={() => setAddOrRemove('add')}>
                      Add
                    </Button>
                    <Button
                      type="button"
                      variant={addOrRemove === 'remove' ? 'primary' : 'secondary'}
                      onClick={() => setAddOrRemove('remove')}>
                      Remove
                    </Button>
                  </div>
                </div>
                {amount !== '' && !amountValid && (
                  <Form.Text className="text-danger">Amount is required (can be $0.00)</Form.Text>
                )}
              </Form.Group>
            </div>
            <div className="col-md-12">
              <Form.Group className="mb-2">
                <Form.Label>Description/Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Description/Notes"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)} />
              </Form.Group>
            </div>
            {addOrRemove === 'add' && (
              <div className="row">
                <div className="col-md-2">
                  <Form.Group className="mb-2">
                    <Form.Label>Notify Inmate</Form.Label>
                    <div className="btn-group d-block" role="group">
                      <Button
                        type="button"
                        variant={notifyInmate ? 'primary' : 'secondary'}
                        onClick={() => setNotifyInmate(true)}>
                        Yes
                      </Button>
                      <Button
                        type="button"
                        variant={!notifyInmate ? 'primary' : 'secondary'}
                        onClick={() => setNotifyInmate(false)}>
                        No
                      </Button>
                    </div>
                  </Form.Group>
                </div>
              </div>
            )}
            {addOrRemove === 'add' && accountType === 'wallet' && (
              <div className="row">
                <div className="col-md-2">
                  <Form.Group className="mb-2">
                    <Form.Label>Charge Rental</Form.Label>
                    <div className="btn-group d-block" role="group">
                      <Button
                        type="button"
                        variant={chargeRental ? 'primary' : 'secondary'}
                        onClick={() => setChargeRental(true)}>
                        Yes
                      </Button>
                      <Button
                        type="button"
                        variant={!chargeRental ? 'primary' : 'secondary'}
                        onClick={() => setChargeRental(false)}>
                        No
                      </Button>
                    </div>
                  </Form.Group>
                </div>
              </div>
            )}
          </div>
        </Card.Body>
        <Card.Footer>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {adding ? 'Adding...' : 'Add'}
          </Button>
        </Card.Footer>
      </Card>
    </Form>
  );
};

export default AddActivityForm;
